/**
 * AddItemButton - Reusable button component for adding new items
 * 
 * Used across exhibits for consistent "Add X" button styling
 * Features blue background with plus icon and customizable text
 */

import React from 'react';
import styled from 'styled-components';
import PlusCircleIcon from '../../assets/icons/ic-plus-circle.svg?react';

// ========================================
// Props Interface
// ========================================

interface AddItemButtonProps {
  onClick?: () => void;
  text: string;
  disabled?: boolean;
}

// ========================================
// Styled Components
// ========================================

const ButtonWrapper = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  height: 40px;
  background: #2563EB;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #1D4ED8;
  }

  &:active {
    background: #1E40AF;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  svg {
    display: inline-flex;
    flex-shrink: 0;
  }

  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
  }

  &:hover:disabled {
    background: #9CA3AF;
  }

  &:active:disabled {
    background: #9CA3AF;
  }

  &:focus:disabled {
    outline: none;
    box-shadow: none;
  }

  &:disabled svg {
    fill: #9CA3AF;
  }
`;

const ButtonText = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.005em;
  text-align: center;
  color: white;

  &:disabled {
    color: #9CA3AF;
  }
`;

// ========================================
// Component
// ========================================

const AddItemButton: React.FC<AddItemButtonProps> = ({ onClick, text, disabled = false }) => {
  return (
    <ButtonWrapper onClick={onClick} disabled={disabled}>
      <PlusCircleIcon />
      <ButtonText>{text}</ButtonText>
    </ButtonWrapper>
  );
};

export default AddItemButton;

